import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SecurityService } from '../../../services/security/security.service';
import { UsuariosService } from '../../../services/usuario/usuarios.service';
import { Usuario } from '../../../shared/models/usuario.model';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-usuarios-roles',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    MultiSelectModule,
    InputTextModule,
    TooltipModule,
    ToastModule
  ],
  templateUrl: './usuarios-roles.component.html',
  styleUrls: ['./usuarios-roles.component.scss']
})
export class UsuariosRolesComponent implements OnInit {
  usuarios = signal<Usuario[]>([]);
  loading = signal<boolean>(false);
  selectedUsuario: Usuario | null = null;
  editingUsuarioId: string | null = null;
  usuarioForm!: FormGroup;
  filterValue = signal<string>('');

  // Roles disponibles para el sistema
  rolesOptions = [
    { label: 'Agente de Publicidad', value: 'ROLE_AGENTE_PUBLICIDAD' },
    { label: 'Admin de Publicidad', value: 'ROLE_ADMIN_PUBLICIDAD' },
    { label: 'Compaginador', value: 'ROLE_COMPAGINADOR' },
    { label: 'Agente de Facturación', value: 'ROLE_AGENTE_FACTURACION' },
    { label: 'Facturación Auditoría', value: 'ROLE_FACTURACION_AUDITORIA' },
    { label: 'Super Admin', value: 'ROLE_SUPER_ADMIN' }
  ];

  private securityService = inject(SecurityService);
  private usuariosService = inject(UsuariosService);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);

  ngOnInit(): void {
    this.initForm();
    this.loadUsuarios();
  }

  initForm(): void {
    this.usuarioForm = this.fb.group({
      roles: [[], Validators.required]
    });
  }

  loadUsuarios(): void {
    this.loading.set(true);
    // Obtener todos los usuarios sin paginación
    this.usuariosService.findAllUsuarios(
      false, // paginate: false para obtener todos
      0,     // page
      1000,  // size: número grande para obtener todos
      '',    // termino: sin filtro
      undefined, // multiSortMeta
      {},    // filtersMetadata
      ''     // accept
    ).subscribe({
      next: (response: any) => {
        this.loading.set(false);
        const usuarios = response['hydra:member'] || response || [];
        // Filtrar roles válidos para cada usuario
        const usuariosConRolesValidos = usuarios.map((usuario: Usuario) => ({
          ...usuario,
          roles: this.filtrarRolesValidos(usuario.roles || [])
        }));
        this.usuarios.set(usuariosConRolesValidos);
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al cargar usuarios:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los usuarios',
          life: 3000
        });
      }
    });
  }

  /**
   * Filtra y valida los roles, eliminando valores inválidos
   */
  private filtrarRolesValidos(roles: any[]): string[] {
    if (!roles || !Array.isArray(roles)) {
      return [];
    }

    const rolesValidosValues = this.rolesOptions.map(opt => opt.value);
    
    return roles
      .filter(role => {
        if (role === null || role === undefined || role === '') return false;
        const roleStr = String(role).trim().toLowerCase();
        if (roleStr === 'null' || roleStr === '') return false;
        return rolesValidosValues.includes(String(role).trim());
      })
      .map(role => String(role).trim())
      .filter((role, index, self) => self.indexOf(role) === index);
  }

  startEdit(usuario: Usuario): void {
    this.editingUsuarioId = usuario.id || null;
    this.selectedUsuario = usuario;
    const rolesValidos = this.filtrarRolesValidos(usuario.roles || []);
    this.usuarioForm.patchValue({
      roles: rolesValidos
    });
  }

  cancelEdit(): void {
    this.editingUsuarioId = null;
    this.selectedUsuario = null;
    this.usuarioForm.reset();
  }

  saveRoles(): void {
    if (!this.selectedUsuario || !this.selectedUsuario.id || this.usuarioForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.usuarioForm.value;
    const rolesValidos = this.filtrarRolesValidos(formValue.roles || []);

    if (rolesValidos.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Error',
        detail: 'Debe seleccionar al menos un rol',
        life: 3000
      });
      return;
    }

    const usuarioActualizado: Usuario = {
      ...this.selectedUsuario,
      roles: rolesValidos
    };

    this.loading.set(true);
    this.usuariosService.guardarUsuario(usuarioActualizado).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Roles actualizados correctamente',
          life: 3000
        });
        
        // Actualizar el usuario en la lista
        const usuarios = this.usuarios();
        const index = usuarios.findIndex(u => u.id === this.selectedUsuario?.id);
        if (index !== -1) {
          usuarios[index] = { ...usuarios[index], roles: rolesValidos };
          this.usuarios.set([...usuarios]);
        }
        
        this.cancelEdit();
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al actualizar roles:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Error al actualizar los roles',
          life: 3000
        });
      }
    });
  }

  getRoleLabel(role: string): string {
    const option = this.rolesOptions.find(opt => opt.value === role);
    return option ? option.label : role;
  }

  getRolesDisplay(roles: string[] | undefined): string {
    if (!roles || roles.length === 0) {
      return 'Sin roles';
    }
    return roles.map(role => this.getRoleLabel(role)).join(', ');
  }

  isEditing(usuarioId: string | undefined): boolean {
    return this.editingUsuarioId === usuarioId;
  }

  canEditRoles(): boolean {
    const userRoles = this.securityService.getUserRoles();
    return userRoles.includes('ROLE_ADMIN') || 
           userRoles.includes('ROLE_SUPER_ADMIN') || 
           userRoles.includes('ROLE_ADMIN_PUBLICIDAD');
  }

  get filteredUsuarios(): Usuario[] {
    const filter = this.filterValue().toLowerCase();
    if (!filter) {
      return this.usuarios();
    }
    return this.usuarios().filter(usuario => {
      const nombre = `${usuario.nombre || usuario.name || ''} ${usuario.apellido || usuario.lastName || ''}`.toLowerCase();
      const email = (usuario.email || '').toLowerCase();
      return nombre.includes(filter) || email.includes(filter);
    });
  }
}

